import { Hono } from "hono";
import type { AppEnv } from "../app";
import type { ApiDeps } from "../types/api-deps";

// Sentinel status used to signal that the handler already wrote to the Node
// ServerResponse directly. writeHonoResponse in server.ts skips piping when
// it sees this status.
export const ALREADY_HANDLED = 597;
const alreadyHandled = () => new Response(null, { status: ALREADY_HANDLED });

export const createSessionRoutes = (deps: ApiDeps) => {
  const app = new Hono<AppEnv>();

  app.use("/:id/*", async (c, next) => {
    if (await deps.isExcludedSubagent?.(c.req.param("id") ?? "")) {
      return c.json({ error: "Session not found" }, 404);
    }
    await next();
  });
  app.use("/:id", async (c, next) => {
    const id = c.req.param("id") ?? "";
    if (
      !["count", "recents", "names", "resume", "start"].includes(id) &&
      (await deps.isExcludedSubagent?.(id))
    ) {
      return c.json({ error: "Session not found" }, 404);
    }
    await next();
  });

  app.get("/count", (c) => {
    deps.handleSessionsCount(c.env.outgoing);
    return alreadyHandled();
  });

  app.get("/recents", (c) => {
    const url = new URL(c.req.url);
    deps.handleGetRecentSessions(url, c.env.outgoing);
    return alreadyHandled();
  });

  app.get("/names", (c) => {
    deps.handleGetSessionNames(c.env.outgoing);
    return alreadyHandled();
  });

  app.post("/resume", async (c) => {
    await deps.handleResume(c.env.incoming, c.env.outgoing);
    return alreadyHandled();
  });

  app.post("/start", async (c) => {
    await deps.handleStartSession(c.env.incoming, c.env.outgoing);
    return alreadyHandled();
  });

  app.get("/", async (c) => {
    const url = new URL(c.req.url);
    await deps.handleListSessions(url, c.env.outgoing);
    return alreadyHandled();
  });

  app.get("/:id/output", (c) => {
    deps.handleGetOutput(c.req.param("id"), c.env.outgoing);
    return alreadyHandled();
  });

  app.post("/:id/input", async (c) => {
    await deps.handleSendInput(c.req.param("id"), c.env.incoming, c.env.outgoing);
    return alreadyHandled();
  });

  app.post("/:id/raw-key", async (c) => {
    await deps.handleRawKey(c.req.param("id"), c.env.incoming, c.env.outgoing);
    return alreadyHandled();
  });

  app.post("/:id/answer", async (c) => {
    await deps.handleSendAnswer(c.req.param("id"), c.env.incoming, c.env.outgoing);
    return alreadyHandled();
  });

  app.post("/:id/prompt/answer", async (c) => {
    await deps.handlePromptAnswer(c.req.param("id"), c.env.incoming, c.env.outgoing);
    return alreadyHandled();
  });

  app.post("/:id/permission/answer", async (c) => {
    await deps.handlePermissionAnswer(c.req.param("id"), c.env.incoming, c.env.outgoing);
    return alreadyHandled();
  });

  app.post("/:id/files", async (c) => {
    await deps.handleUploadFile(c.req.param("id"), c.env.incoming, c.env.outgoing);
    return alreadyHandled();
  });

  app.post("/:id/cancel", (c) => {
    deps.handleCancel(c.req.param("id"), c.env.outgoing);
    return alreadyHandled();
  });

  app.patch("/:id/name", async (c) => {
    await deps.handleSetSessionName(c.req.param("id"), c.env.incoming, c.env.outgoing);
    return alreadyHandled();
  });

  app.patch("/:id/model", async (c) => {
    await deps.handleSetSessionModel(c.req.param("id"), c.env.incoming, c.env.outgoing);
    return alreadyHandled();
  });

  app.patch("/:id/effort", async (c) => {
    await deps.handleSetSessionEffort(c.req.param("id"), c.env.incoming, c.env.outgoing);
    return alreadyHandled();
  });

  app.post("/:id/adopt", async (c) => {
    await deps.handleAdopt(c.req.param("id"), c.env.outgoing);
    return alreadyHandled();
  });

  app.post("/:id/fork", async (c) => {
    await deps.handleFork(c.req.param("id"), c.env.incoming, c.env.outgoing);
    return alreadyHandled();
  });

  app.post("/:id/stop", async (c) => {
    const when = c.req.query("when") === "idle" ? "idle" : "now";
    const ignoreWatchers = c.req.query("ignoreWatchers") === "true";
    const deleteConversation = c.req.query("delete") === "true";
    await deps.handleStopSession(c.req.param("id"), c.env.outgoing, {
      when,
      ignoreWatchers,
      delete: deleteConversation,
    });
    return alreadyHandled();
  });

  app.post("/:id/kill", async (c) => {
    const deleteConversation = c.req.query("delete") === "true";
    await deps.handleKillSession(c.req.param("id"), c.env.outgoing, { delete: deleteConversation });
    return alreadyHandled();
  });

  app.get("/:id", async (c) => {
    await deps.handleGetSession(c.req.param("id"), c.env.outgoing);
    return alreadyHandled();
  });

  return app;
};
